import { LiksButton } from '../../../components/liks/LiksButton/LiksButton';
import { useI18n } from '../../../i18n/useI18n';
import { formatCost } from '../../../utils/formatUtils';
import './CostSummary.css';

export type CostSummaryProps = {
  isLoading?: boolean;
  isError?: boolean;
  /** When not loading/error, `false` shows the empty state. */
  hasData?: boolean;
  monthlyEstimatedCosts?: number | null;
  yearlyEstimatedCosts?: number | null;
  monthlyCostLimit?: number | null;
  yearlyCostLimit?: number | null;
  onOpenPrices: () => void;
  className?: string;
};

function isPositiveLimit(value: number | null | undefined): value is number {
  return value != null && Number.isFinite(value) && value > 0;
}

function safeCost(value: number | null | undefined): number {
  return value != null && Number.isFinite(value) ? Math.max(0, value) : 0;
}

type LimitProgressProps = {
  label: string;
  estimatedCosts: number | null | undefined;
  costLimit: number;
  usageAriaLabel: string;
  exceededLabel: string;
  locale: string;
  percentTemplate: (percent: string) => string;
};

function LimitProgress({
  label,
  estimatedCosts,
  costLimit,
  usageAriaLabel,
  exceededLabel,
  locale,
  percentTemplate,
}: LimitProgressProps) {
  const costs = safeCost(estimatedCosts);
  const usageRatio = costs / costLimit;
  const usagePercent = Math.round(usageRatio * 100);
  const barPercent = Math.min(100, Math.max(0, usageRatio * 100));
  const isExceeded = usageRatio > 1;
  const isWarning = !isExceeded && usageRatio >= 0.8;
  const valueLabel = `${formatCost(estimatedCosts, locale)} / ${formatCost(costLimit, locale)}`;

  return (
    <div
      className={[
        'cost-summary__metric',
        isExceeded && 'cost-summary__metric--exceeded',
        isWarning && 'cost-summary__metric--warning',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="cost-summary__heading">
        <span className="cost-summary__cost-label">{label}</span>
        {isExceeded ? (
          <span className="cost-summary__exceeded-hint">{exceededLabel}</span>
        ) : null}
      </div>

      <div className="cost-summary__progress">
        <span className="cost-summary__cost-value">{valueLabel}</span>
        <div className="cost-summary__bar-wrap">
          <div
            className="cost-summary__track"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.min(100, Math.max(0, usagePercent))}
            aria-label={usageAriaLabel}
          >
            <div
              className="cost-summary__fill"
              style={{ width: `${barPercent}%` }}
            />
          </div>
          <span className="cost-summary__bar-pct">
            {percentTemplate(String(usagePercent))}
          </span>
        </div>
      </div>
    </div>
  );
}

export function CostSummary({
  isLoading = false,
  isError = false,
  hasData = true,
  monthlyEstimatedCosts,
  yearlyEstimatedCosts,
  monthlyCostLimit = null,
  yearlyCostLimit = null,
  onOpenPrices,
  className,
}: CostSummaryProps) {
  const { locale, translate } = useI18n();

  if (isLoading) {
    return (
      <p
        className={['cost-summary__status', className]
          .filter(Boolean)
          .join(' ')}
      >
        {translate('organisationManagement.costs.loading')}
      </p>
    );
  }

  if (isError) {
    return (
      <p
        className={['cost-summary__status', className]
          .filter(Boolean)
          .join(' ')}
        role="alert"
      >
        {translate('organisationManagement.costs.loadError')}
      </p>
    );
  }

  if (!hasData) {
    return (
      <p
        className={['cost-summary__status', className]
          .filter(Boolean)
          .join(' ')}
      >
        {translate('organisationManagement.costs.empty')}
      </p>
    );
  }

  const hasMonthlyLimit = isPositiveLimit(monthlyCostLimit);
  const hasYearlyLimit = isPositiveLimit(yearlyCostLimit);
  const percentTemplate = (percent: string) =>
    translate('organisationManagement.costs.limitUsagePercent', { percent });
  const exceededLabel = translate(
    'organisationManagement.costs.costLimitExceeded',
  );

  return (
    <div className={['cost-summary', className].filter(Boolean).join(' ')}>
      <div className="cost-summary__metrics">
        {hasMonthlyLimit ? (
          <LimitProgress
            label={translate(
              'organisationManagement.costs.estimatedMonthlyCosts',
            )}
            estimatedCosts={monthlyEstimatedCosts}
            costLimit={monthlyCostLimit}
            usageAriaLabel={translate(
              'organisationManagement.costs.monthlyLimitUsage',
            )}
            exceededLabel={exceededLabel}
            locale={locale}
            percentTemplate={percentTemplate}
          />
        ) : (
          <div className="cost-summary__metric">
            <div className="cost-summary__heading">
              <span className="cost-summary__cost-label">
                {translate(
                  'organisationManagement.costs.estimatedMonthlyCosts',
                )}
              </span>
            </div>
            <div className="cost-summary__progress">
              <span className="cost-summary__cost-value">
                {formatCost(monthlyEstimatedCosts, locale)}
              </span>
            </div>
          </div>
        )}

        {hasYearlyLimit ? (
          <LimitProgress
            label={translate(
              'organisationManagement.costs.estimatedYearlyCosts',
            )}
            estimatedCosts={yearlyEstimatedCosts}
            costLimit={yearlyCostLimit}
            usageAriaLabel={translate(
              'organisationManagement.costs.yearlyLimitUsage',
            )}
            exceededLabel={exceededLabel}
            locale={locale}
            percentTemplate={percentTemplate}
          />
        ) : null}
      </div>

      <div className="cost-summary__actions">
        <LiksButton variant="secondary" onClick={onOpenPrices}>
          {translate('organisationManagement.costs.prices.button')}
        </LiksButton>
      </div>
    </div>
  );
}
